import { buildCollection } from "@firecms/core";
import { LatLngField } from "../components/LatLngField";

export const LOCATIONS_PATH = "setting/directory/locations";

// Fetch cap for pickers/lookups over this collection. Must comfortably exceed
// the seeded dataset (~380 US metro areas) or lists truncate silently.
export const LOCATIONS_FETCH_LIMIT = 1000;

const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// DR-447: drives the directory query strategy in astro — city pages keep the
// GEO_DISTANCE radius query, state/country pages switch to exact string
// filters on the Vertex location_state / location_country fields.
export const LOCATION_TYPES = ["city", "state", "country"] as const;
export type LocationType = typeof LOCATION_TYPES[number];

export const locationsCollection = buildCollection<any>({
    id: "locations",
    name: "Directory Locations",
    singularName: "Location",
    icon: "location_on",
    description: "Locations attachable to directory pages (setting/directory/locations). The document ID is the location slug, e.g. austin-tx.",
    path: LOCATIONS_PATH,
    defaultSize: "xs",
    // The document ID is the kebab-case location slug (e.g. austin-tx),
    // validated and guarded against overwrites in onPreSave
    customId: true,
    properties: {
        name: {
            dataType: "string",
            name: "Name",
            description: "Display name, e.g. Austin, TX",
            validation: { required: true }
        },
        type: {
            dataType: "string",
            name: "Type",
            description: "Location granularity — city directories query by radius, state/country directories by exact match (DR-447)",
            validation: { required: true },
            enumValues: {
                city: "City",
                state: "State",
                country: "Country"
            }
        },
        latlng: {
            dataType: "geopoint",
            name: "Coordinates",
            validation: { required: true },
            Field: LatLngField
        },
        default_range_miles: {
            dataType: "number",
            name: "Default range (miles)",
            description: "Default search radius; overridable per directory",
            validation: { required: true, min: 1 }
        },
        enabled: {
            dataType: "boolean",
            name: "Enabled",
            description: "Availability toggle for CMS pickers",
            defaultValue: true
        },
        created_at: {
            dataType: "date",
            name: "Created at",
            autoValue: "on_create"
        },
        updated_at: {
            dataType: "date",
            name: "Updated at",
            autoValue: "on_update"
        }
    },
    callbacks: {
        onPreSave: async ({ values, entityId, status, context }: any) => {
            if (!entityId || !SLUG_REGEX.test(entityId)) {
                throw new Error("Document ID must be a kebab-case slug (lowercase letters, numbers, single hyphens), e.g. austin-tx");
            }
            // FireCMS would silently overwrite an existing doc when a taken ID
            // is typed for a new entity — reject that explicitly
            if (status === "new" || status === "copy") {
                try {
                    const existing = await context.dataSource.fetchEntity({
                        path: LOCATIONS_PATH,
                        entityId,
                        collection: locationsCollection
                    });
                    if (existing?.values) {
                        throw new Error(`A location with the ID "${entityId}" already exists`);
                    }
                } catch (e: any) {
                    if (e?.message?.includes("already exists")) throw e;
                    // fetch errors (e.g. doc missing) mean the ID is free
                }
            }
            const g = values.latlng;
            if (!g || typeof g.latitude !== "number" || typeof g.longitude !== "number") {
                throw new Error("Latitude and longitude are both required");
            }
            if (g.latitude < -90 || g.latitude > 90) {
                throw new Error("Latitude must be between -90 and 90");
            }
            if (g.longitude < -180 || g.longitude > 180) {
                throw new Error("Longitude must be between -180 and 180");
            }
            if (typeof values.default_range_miles !== "number" || values.default_range_miles <= 0) {
                throw new Error("Default range must be a positive number of miles");
            }
            if (!LOCATION_TYPES.includes(values.type)) {
                throw new Error(`Type must be one of: ${LOCATION_TYPES.join(", ")}`);
            }
            return values;
        }
    },
    additionalFields: [
        {
            key: "id",
            name: "ID",
            Builder: ({ entity }) => entity.id
        }
    ],
    propertiesOrder: [
        "name",
        "type",
        "latlng",
        "default_range_miles",
        "enabled",
        "created_at",
        "updated_at"
    ]
});
